import { useState } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { tv } from 'tailwind-variants';

import { updateScore } from 'features/learners/slice';
import { selectCurrentUser } from 'features/auth/slice';
import useSolutions from 'lib/hooks/useSolutions';

import {
  SUBMISSION_GUIDE_URL,
  TRUSTED_HOSTING_PROVIDERS,
} from 'features/pages/slice';

import {
  Card,
  flash,
  Heading,
  Icon,
  LoadingScreen,
  Rule,
} from '@frontendmentorio/design-system-v2';

import Form from 'features/solutions/Form';
import UnlockProCard from 'features/shared/UnlockProCard';

const styles = tv({
  base: ['flex-col', 'gap-5', 'hidden', 'items-start', 'desktop:flex-row'],
  variants: {
    isActive: {
      true: ['flex'],
    },
  },
});

export default function New({ challenge, isActive }) {
  const { create } = useSolutions();
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);

  const handleSubmit = async (data) => {
    setIsLoading(true);

    try {
      await create({ ...data, challenge: challenge.id });
      await dispatch(updateScore(challenge.difficulty * 10));

      flash.success('You completed the challenge!');
      router.push(`/challenges/${challenge.slug}/?tab=solutions&share=true`);
    } catch (error) {
      flash.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles({ isActive })}>
      {isLoading && <LoadingScreen />}

      <Form onSubmit={handleSubmit} />

      <div className="flex flex-col gap-5 desktop:max-w-[20rem] sticky top-5">
        {!currentUser?.hasActiveSubscription && (
          <UnlockProCard
            heading={
              <>
                Get AI-powered insights on every submission{' '}
                <span aria-hidden="true">🤖</span>
              </>
            }
          >
            <p>
              Pro members receive instant, personalized code reviews powered by AI to help level up faster.
            </p>
          </UnlockProCard>
        )}

        <Card className="flex flex-col gap-4 p-5">
        <Heading className="font-semibold" level="5" tagName="h2">
          Helpful articles
        </Heading>

        <Rule tagName="span" />

        <ul className="flex flex-col gap-3">
          <li className="flex flex-col gap-3">
            <NextLink
              className="flex gap-3 items-center justify-between hover:underline"
              href={SUBMISSION_GUIDE_URL}
              rel="noopener"
              target="_blank"
            >
              <span>
                A complete guide to submitting solutions{' '}
                <span className="sr-only">(opens in new tab)</span>
              </span>

              <Icon
                className="text-neutral-500 text-sm"
                name="arrow-up-right-from-square"
              />
            </NextLink>

            <Rule tagName="span" />
          </li>

          <li className="flex flex-col gap-3">
            <NextLink
              className="flex gap-3 items-center justify-between hover:underline"
              href={TRUSTED_HOSTING_PROVIDERS}
              rel="noopener"
              target="_blank"
            >
              <span>
                Our recommended hosting providers{' '}
                <span className="sr-only">(opens in new tab)</span>
              </span>

              <Icon
                className="text-neutral-500 text-sm"
                name="arrow-up-right-from-square"
              />
            </NextLink>
          </li>
        </ul>
        </Card>
      </div>
    </div>
  );
}
